import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/dist/query/react';
import { logout, tokenReceived } from '../features/auth/authSlice';
import { refrescarSesion } from './refrescarSesion';
import { RootState } from '../redux/store';
import { settings } from '../settings';
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { BASE_URLS } from './consts';
import { sanitizeParams } from '../utils/sanitizeParams';

interface AssignEstablishmentsRequest {
    idUsuario: number;
    idsEstablecimientos: number[];
}

interface CompanyRolesResponse {
    perfiles: [
        {
            id: number;
            nombre: string;
        }
    ];
    categoriasFiscales: [
        {
            id: number;
            nombre: string;
        }
    ];
    organizaciones: [
        {
            id: number;
            abreviatura: string;
            colorPrincipal: string;
            imagenes: string;
            nombre: string;
        }
    ];
}

interface Establishment {
    id: number;
    nombre: string;
    direccion: {
        calle: string;
        altura: string;
        localidad: {
            id: number;
            nombre: string;
        };
    };
    organismo?: {
        id: number;
        nombre: string;
        organismoCentral?: { id: number; nombre: string; contrato: string } | null;
    } | null;
    selected: boolean;
}

interface EstablishmentsResponse {
    establecimientos: Establishment[];
}

const baseQuery = fetchBaseQuery({
    baseUrl: `${settings.url.host}${settings.url.api}`,
    credentials: 'include',
    prepareHeaders: (headers, { getState }) => {
        const accessToken = (getState() as RootState).auth.accessToken;
        if (accessToken) {
            headers.set('authorization', `Bearer ${accessToken}`);
        }
        return headers;
    }
});

const baseQueryWithReauth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
    args,
    api,
    extraOptions
) => {
    let result: any = await baseQuery(args, api, extraOptions);

    if (result.error && result.error.status === 401) {
        const refreshResult = await refrescarSesion(baseQuery, api, extraOptions);
        if (refreshResult?.data) {
            api.dispatch(tokenReceived(refreshResult.data));
            result = await baseQuery(args, api, extraOptions);
        } else {
            api.dispatch(logout());
        }
    }
    return result;
};

export const companyApi = createApi({
    reducerPath: 'empresa',
    tagTypes: ['Empresas', 'EmpresaById'],
    baseQuery: baseQueryWithReauth,
    endpoints: (builder) => ({
        createEstablishment: builder.mutation({
            query: (body) => ({
                url: `${BASE_URLS.ESTABLISHMENTS}`,
                method: 'POST',
                body: body
            })
        }),
        editEmpresa: builder.mutation({
            query: ({ body, idEmpresa }) => ({
                url: `${BASE_URLS.ESTABLISHMENTS}/${idEmpresa}`,
                method: 'PATCH',
                body
            }),
            invalidatesTags: (_, error) => (error ? [] : ['Empresas', 'EmpresaById'])
        }),
        editEmpresaState: builder.mutation({
            query: ({ body, idEmpresa }) => ({
                url: `${BASE_URLS.ESTABLISHMENTS}/${idEmpresa}/estado`,
                method: 'PATCH',
                body
            }),
            invalidatesTags: (_, error) => (error ? [] : ['Empresas', 'EmpresaById'])
        }),
        deRegisterEstaglishmentState: builder.mutation({
            query: ({ body, establishmentId }) => ({
                url: `${BASE_URLS.ESTABLISHMENTS}/${establishmentId}/baja`,
                method: 'POST',
                body
            }),
            invalidatesTags: (_, error) => (error ? [] : ['Empresas', 'EmpresaById'])
        }),
        getEmpresas: builder.query({
            query: (params) => {
                const cleanParams = sanitizeParams(params);

                return {
                    url: `${BASE_URLS.ESTABLISHMENTS}`,
                    params: cleanParams
                };
            },
            keepUnusedDataFor: 5,
            providesTags: ['Empresas']
        }),
        // Ids de la grilla sin paginar, con los mismos filtros y solapa (SMP-512): los usa la
        // descarga masiva de PDF de visitas
        getIdsEstablecimientos: builder.query<{ data: { ids: number[]; count: number } }, any>({
            query: (params) => ({
                url: `${BASE_URLS.ESTABLISHMENTS}/ids`,
                params: sanitizeParams(params)
            }),
            keepUnusedDataFor: 0
        }),
        /**
         * Trae todos los establecimientos que cumplen los filtros, sin un `take` fijo
         * (mismo patrón que `getTodosLosOrganismos`): primero pide uno solo para conocer
         * el total filtrado (`count`) y, si hay más, los pide todos. Evita asumir que un
         * `take` grande pero fijo alcanza a traer el universo completo (SMP-429).
         */
        getTodosLosEstablecimientos: builder.query<any, Record<string, any>>({
            async queryFn(filtros, _api, _extraOptions, fetchWithBQ) {
                const cleanParams = sanitizeParams(filtros);
                const primera = await fetchWithBQ({
                    url: `${BASE_URLS.ESTABLISHMENTS}`,
                    params: { ...cleanParams, skip: 0, take: 1 }
                });
                if (primera.error) return { error: primera.error };

                const pagina = (primera.data as any)?.data;
                const count = pagina?.count ?? 0;
                if (count <= (pagina?.data?.length ?? 0)) return { data: primera.data };

                const todos = await fetchWithBQ({
                    url: `${BASE_URLS.ESTABLISHMENTS}`,
                    params: { ...cleanParams, skip: 0, take: count }
                });
                return todos.error ? { error: todos.error } : { data: todos.data };
            },
            keepUnusedDataFor: 5,
            providesTags: ['Empresas']
        }),
        getCertificatesByIdBuque: builder.query({
            query: (idBuque) => ({ url: `buques/${idBuque}/certificados` }),
            transformResponse: (response: { data: any }) => response?.data
        }),
        getEmpresa: builder.query({
            query: (idEmpresa) => ({ url: `${BASE_URLS.ESTABLISHMENTS}/${idEmpresa}` }),
            providesTags: ['EmpresaById'],
            transformResponse: (response: { data: any }) => response?.data
        }),
        getComunicaciones: builder.query({
            query: ({ skip, take, nombre, fechaInicio, fechaFin }) => ({
                url: 'notificaciones',
                params: { skip, take, nombre, fechaInicio, fechaFin }
            }),
            providesTags: ['Empresas']
        }),
        editComunicacionCertificado: builder.mutation({
            query: ({ body, idCertificado }) => ({
                url: `/empresas/comunicaciones/certificados/${idCertificado}`,
                method: 'PATCH',
                body
            }),
            invalidatesTags: (_, error) => (error ? [] : ['Empresas'])
        }),
        getComunicacionesEventos: builder.query({
            query: () => ({ url: '/empresas/comunicaciones/eventos' }),
            providesTags: ['Empresas']
        }),
        editComunicacionEventos: builder.mutation({
            query: ({ body, id }) => ({
                url: `/empresas/comunicaciones/eventos/${id}`,
                method: 'PATCH',
                body
            }),
            invalidatesTags: (_, error) => (error ? [] : ['Empresas'])
        }),
        editComunicacionEventosUsers: builder.mutation({
            query: ({ body, id }) => ({
                url: `/empresas/comunicaciones/eventos/${id}/usuarios`,
                method: 'PATCH',
                body
            }),
            invalidatesTags: (_, error) => (error ? [] : ['Empresas'])
        }),
        editComunicacionEventosPerfil: builder.mutation({
            query: ({ body, idParametroEventoPerfil }) => ({
                url: `/empresas/comunicaciones/eventos/perfil/${idParametroEventoPerfil}`,
                method: 'PATCH',
                body
            }),
            invalidatesTags: (_, error) => (error ? [] : ['Empresas'])
        }),
        assignEstablishmentsToUser: builder.mutation({
            query: ({ idUsuario, idsEstablecimientos }: AssignEstablishmentsRequest) => ({
                url: `/establecimientos/usuarios/${idUsuario}`,
                method: 'POST',
                body: { idsEstablecimientos }
            }),
            invalidatesTags: ['EmpresaById']
        }),
        getEstablishmentDetail: builder.query({
            query: ({ establishmentId }) => ({
                url: `${BASE_URLS.ESTABLISHMENTS}/${establishmentId}`
            }),
            providesTags: ['EmpresaById'],
            transformResponse: (response: { data: any }) => response?.data
        }),
        getCIIUCodes: builder.query({
            query: (params) => ({
                url: `${BASE_URLS.ESTABLISHMENTS}/codigos-ciiu`,
                params
            }),
            providesTags: ['Empresas']
        }),
        getProvinces: builder.query({
            query: () => ({
                url: `${BASE_URLS.ESTABLISHMENTS}/provincias`,
                transformResponse: (response: { data: any }) => response?.data?.provincias
            })
        }),
        getCities: builder.query({
            query: ({ provinceId }) => ({
                url: `${BASE_URLS.ESTABLISHMENTS}/provincias/${provinceId}/localidades`
            }),
            providesTags: ['Empresas']
        }),
        getPostalCodes: builder.query({
            query: ({ cityId }) => ({
                url: `${BASE_URLS.ESTABLISHMENTS}/ciudades/${cityId}/codigos-postal`
            }),
            providesTags: ['Empresas']
        }),
        getEstablishmentsByUser: builder.query<EstablishmentsResponse, { idUsuario: number }>({
            query: ({ idUsuario }) => ({
                url: `/establecimientos/usuarios/${idUsuario}`
            }),
            providesTags: ['EmpresaById'],
            keepUnusedDataFor: 5,
            transformResponse: (response: { data: any }) => response.data
        }),
        getEstablishmentsIndicators: builder.query({
            query: ({ idOrganismoCentral, idOrganizacion }) => ({
                url: `establecimientos/indicadores`,
                params: { idOrganismoCentral, idOrganizacion }
            }),
            providesTags: ['Empresas'],
            transformResponse: (response: { data: any }) => response?.data,
            keepUnusedDataFor: 5
        }),
        getEstablishmentDataBySRT: builder.query({
            query: (params: { codigo: number; cuit: string }) => ({
                url: `establecimientos/srt`,
                params
            }),
            transformResponse: (response?: { data?: any }) =>
                response?.data?.dataSRT
                    ? {
                          ...response.data.dataSRT,
                          organismosContrato: response.data.organismosContrato ?? []
                      }
                    : null
        }),
        getSrtEstablishmentsByCUIT: builder.query({
            query: (cuit: string) => ({
                url: `establecimientos/srt/cuit/${cuit}`
            })
        }),
        submitMassiveUploadSRT: builder.mutation({
            query: (body) => ({
                url: `/establecimientos/alta-masiva`,
                method: 'POST',
                body
            })
        }),
        getEstablishmentHistory: builder.query({
            query: ({ establishmentId }: { establishmentId: number }) => ({
                url: `establecimientos/${establishmentId}/historial`,
                params: { pending: true }
            })
        }),
        getEstablishmentIncompleteSRTDataHistory: builder.query({
            query: ({ establishmentId }: { establishmentId: number }) => ({
                url: `establecimientos/${establishmentId}/historial`
            })
        }),
        exportEstablishments: builder.mutation<Blob, any>({
            query: (body) => ({
                url: '/establecimientos/exportar',
                method: 'POST',
                body,
                // El back manda el xlsx como binario; los errores siguen viniendo en JSON
                responseHandler: (response) => (response.ok ? response.blob() : response.json())
            })
        }),
        getEstablishmentDeregisterMotives: builder.query({
            query: () => ({
                url: `establecimientos/motivos-baja`
            })
        }),
        getEstablishmentActivities: builder.query({
            query: () => ({
                url: `establecimientos/tipos-actividad`
            })
        }),
        getCIIURevisions: builder.query({
            query: () => ({
                url: `establecimientos/codigos-ciiu-revisiones`
            })
        })
    })
});

export const {
    useGetEmpresaQuery,
    useCreateEstablishmentMutation,
    useEditEmpresaMutation,
    useDeRegisterEstaglishmentStateMutation,
    useEditEmpresaStateMutation,
    useGetEmpresasQuery,
    useGetTodosLosEstablecimientosQuery,
    useLazyGetTodosLosEstablecimientosQuery,
    useLazyGetEstablishmentDetailQuery,
    useLazyGetEmpresasQuery,
    useLazyGetIdsEstablecimientosQuery,
    useLazyGetEmpresaQuery,
    useGetComunicacionesQuery,
    useEditComunicacionCertificadoMutation,
    useGetComunicacionesEventosQuery,
    useEditComunicacionEventosMutation,
    useEditComunicacionEventosUsersMutation,
    useEditComunicacionEventosPerfilMutation,
    useAssignEstablishmentsToUserMutation,
    useGetEstablishmentDetailQuery,
    useGetCIIUCodesQuery,
    useGetProvincesQuery,
    useLazyGetProvincesQuery,
    useGetCitiesQuery,
    useLazyGetCitiesQuery,
    useGetPostalCodesQuery,
    useLazyGetPostalCodesQuery,
    useGetEstablishmentsByUserQuery,
    useGetEstablishmentsIndicatorsQuery,
    useLazyGetEstablishmentDataBySRTQuery,
    useLazyGetSrtEstablishmentsByCUITQuery,
    useSubmitMassiveUploadSRTMutation,
    useGetEstablishmentDataBySRTQuery,
    useGetEstablishmentHistoryQuery,
    useGetEstablishmentIncompleteSRTDataHistoryQuery,
    useGetEstablishmentDeregisterMotivesQuery,
    useExportEstablishmentsMutation,
    useGetEstablishmentActivitiesQuery,
    useGetCIIURevisionsQuery
} = companyApi;
